import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription, lastValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  GraphService,
  ResourceStatus,
} from 'src/app/services/graph/graph.service';
import { ResourceListFields } from '../../../../models/resource-list-fields.model';
import { entAnimations } from 'src/app/animations';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { SettingsService } from 'src/app/services/settings/settings.service';

@Component({
  selector: 'app-resource-detail',
  templateUrl: './resource-detail.component.html',
  styleUrls: ['./resource-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class ResourceDetailComponent implements OnInit, OnDestroy {
  // Route Parameters Subscription
  private subscription: Subscription = new Subscription();

  ressourceid: string;
  isLoading = false;

  isErrorUpdateRessource = false;
  isErrorGetRessource = false;
  isBusy = false;

  isBusyUpdateRessource = false;
  error: Error = { error: { message: '', code: '' } };
  errorUpdateRessource: Error = { error: { message: '', code: '' } };
  errorGetRessource: Error = { error: { message: '', code: '' } };

  selectedRessource: ResourceListFields;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  // Pwd
  ressourceform: FormGroup;
  isRessourceUpdated = false;

  // Form to Bolean
  displayInGal = false;
  ajout_Directeur = false;
  ajout_ConseilDir = false;
  ajout_secDir = false;
  ajout_admin = false;
  ajout_Tech = false;
  ajout_ITRes = false;
  ajout_ITGest = false;
  ajout_PersonEns = false;

  access_Directeur = false;
  access_ConseilDir = false;
  access_secDir = false;
  access_admin = false;
  access_Tech = false;
  access_ITRes = false;
  access_ITGest = false;
  isDisabled = false;
  entAccessEdit = false;

  constructor(
    private _graphService: GraphService,
    private _activatedRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _settingsService: SettingsService
  ) {}

  ngOnInit() {
    this.isLoading = false;
    this.isBusy = false;

    this.subscription.add(
      this._settingsService.currentGE$.subscribe(() => {
        this._router.navigate(['/exchange/resources']);
      })
    );

    this.subscription.add(
      this._activatedRoute.params.subscribe(params => {
        this.ressourceid = params['id'];

        if (this.ressourceid == null) {
          this._router.navigate(['/exchange/resources']);
        }
      })
    );

    this.getRessourceById();

    this.ressourceform = this._formBuilder.group({
      title: [''],
      capacity: ['', Validators.pattern('^[0-9]*$')], // Only number
      address: [''],
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscription.unsubscribe();
  }

  private setDefaultFormValue(): void {
    // Set Default value for Step 1
    this.ressourceform.get('title').setValue(this.selectedRessource.title);
    this.ressourceform
      .get('capacity')
      .setValue(this.selectedRessource.capacit_x00e9_);
    this.ressourceform.get('address').setValue(this.selectedRessource.rue);

    this.displayInGal = this.validateBoolean(
      this.selectedRessource.displayInGAL
    );

    this.ajout_admin = this.validateBoolean(
      this.selectedRessource.rqarbipGrAdministratif
    );
    this.ajout_ConseilDir = this.validateBoolean(
      this.selectedRessource.rqarbipGrConseilDirection
    );
    this.ajout_Directeur = this.validateBoolean(
      this.selectedRessource.rqarbipGrDirecteur
    );
    this.ajout_ITGest = this.validateBoolean(
      this.selectedRessource.rqarbipGrITGestionnaire
    );
    this.ajout_ITRes = this.validateBoolean(
      this.selectedRessource.rqarbipGrITResponsable
    );
    this.ajout_PersonEns = this.validateBoolean(
      this.selectedRessource.rqarbipGrPersonnelEnseignant
    );
    this.ajout_secDir = this.validateBoolean(
      this.selectedRessource.rqarbipGrSecretaireDirection
    );
    this.ajout_Tech = this.validateBoolean(
      this.selectedRessource.rqarbipGrTechnique
    );

    this.access_admin = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrAdministratif
    );
    this.access_ConseilDir = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrConseilDirection
    );
    this.access_Directeur = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrDirecteur
    );
    this.access_ITGest = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrITGestionnaire
    );
    this.access_ITRes = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrITResponsable
    );
    this.access_secDir = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrSecretaireDirect
    );
    this.access_Tech = this.validateBoolean(
      this.selectedRessource.rqarFullAccessGrTechnique
    );

    this.entAccessEdit = this.validateBoolean(
      this.selectedRessource.entAccessEdit
    );

    if (this.entAccessEdit) {
      this.isDisabled = false;
    } else {
      this.isDisabled = true;
      this.ressourceform.get('title').disable();
      this.ressourceform.get('capacity').disable();
      this.ressourceform.get('address').disable();
    }
  }

  private validateBoolean(input: string | null | undefined): boolean {
    return input === 'True' ? true : false;
  }

  private getUpdatedResources(): ResourceListFields {
    // Reset all errors and boolean in case of multiple error
    this.isBusy = false;

    return {
      // Standard Config
      type: 'Ressource',
      etat_Ressource: ResourceStatus.MODIFY,

      // Forms
      title: this.ressourceform.get('title').value as string,
      capacit_x00e9_: this.ressourceform.get('capacity').value as string,
      rue: this.ressourceform.get('address').value as string,

      // Checkbox
      displayInGAL: this.firstCharToUpper(this.displayInGal),

      // Personnel technique
      rqarbipGrTechnique: this.firstCharToUpper(this.ajout_Tech),
      rqarFullAccessGrTechnique: this.firstCharToUpper(this.access_Tech),

      // Personnel administratif
      rqarbipGrAdministratif: this.firstCharToUpper(this.ajout_admin),
      rqarFullAccessGrAdministratif: this.firstCharToUpper(this.access_admin),

      // Secrétaire de direction
      rqarbipGrSecretaireDirection: this.firstCharToUpper(this.ajout_secDir),
      rqarFullAccessGrSecretaireDirect: this.firstCharToUpper(
        this.access_secDir
      ),

      // IT-Responsable
      rqarbipGrITResponsable: this.firstCharToUpper(this.ajout_ITRes),
      rqarFullAccessGrITResponsable: this.firstCharToUpper(this.access_ITRes),

      // IT-Gestionnaire
      rqarbipGrITGestionnaire: this.firstCharToUpper(this.ajout_ITGest),
      rqarFullAccessGrITGestionnaire: this.firstCharToUpper(this.access_ITGest),

      // Directeur
      rqarbipGrDirecteur: this.firstCharToUpper(this.ajout_Directeur),
      rqarFullAccessGrDirecteur: this.firstCharToUpper(this.access_Directeur),

      // Conseil de Direction
      rqarbipGrConseilDirection: this.firstCharToUpper(this.ajout_ConseilDir),
      rqarFullAccessGrConseilDirection: this.firstCharToUpper(
        this.access_ConseilDir
      ),

      // Personnel enseignant
      rqarbipGrPersonnelEnseignant: this.firstCharToUpper(this.ajout_PersonEns),
    };
  }

  private firstCharToUpper(input: string | boolean): string {
    return String(input).charAt(0).toUpperCase() + String(input).slice(1);
  }

  async updateRessource(): Promise<void> {
    this.errorUpdateRessource = { error: { message: '', code: '' } };
    this.isErrorUpdateRessource = false;
    this.isRessourceUpdated = false;
    this.isBusyUpdateRessource = true;

    try {
      const updatedResource = this.getUpdatedResources();
      await lastValueFrom(
        this._graphService.updateRessource(this.ressourceid, updatedResource)
      ).then(() => {
        this.isRessourceUpdated = true;
        this.isBusyUpdateRessource = false;
      });
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (error.error === undefined) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        } else {
          if (customError.error.code == 'ERR403') {
            this._router.navigate(['/errors/error-403']);
          } else {
            this._router.navigate(['/errors/error-404']);
          }
        }
      }

      this.isErrorUpdateRessource = true;
      this.errorUpdateRessource = customError;
      this.isRessourceUpdated = false;
      this.isBusyUpdateRessource = false;
    }
  }

  async getRessourceById(): Promise<void> {
    try {
      this.isLoading = true;
      this.selectedRessource = await lastValueFrom(
        this._graphService.getRessourceById(this.ressourceid)
      );
      if (this.selectedRessource.etat_Ressource != ResourceStatus.UPTODATE) {
        this._router.navigate(['exchange/resources']);
      }

      this.setDefaultFormValue();
      this.isErrorGetRessource = false;
      this.errorGetRessource = { error: { message: '', code: '' } };
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (customError.error !== undefined) {
          if (customError.error.code == 'ERR403') {
            this._router.navigate(['/errors/error-403']);
          } else {
            this._router.navigate(['/errors/error-404']);
          }
        }
      }

      this.isErrorGetRessource = true;
      this.errorGetRessource = customError;
    } finally {
      this.isLoading = false;
    }
  }
}
